/**
 * `PlanetStage` — the narrative backdrop's three.js scene: one procedural world, a thin atmosphere,
 * a ring of orbital traffic and a starfield, drawn into a canvas that covers the viewport. Which
 * world, and which optional layers (the atmosphere shell, night lights, Aubade's wind), comes from
 * the pinned entry's profile (`planetProfiles.ts`).
 *
 * Nothing is downloaded. At construction the surface and cloud maps are baked once from noise into
 * render targets (`planetShaders.ts`); each frame then only lights them. The planet spins by the
 * pure `spin.ts` model — the pointer drives it, inertia carries a fling, and after a beat the planet
 * settles to its rest angle. Everything moving (the spin, the clouds, the traffic, the wind) advances
 * on the model's one clock, `dt * timeScale`. In **play** the slow idle spin carries on; in **pause** (the
 * default, `setMode`) the clock then glides to 0 and the loop stops drawing until a drag wakes it.
 * The slow spin and the glide draw at {@link IDLE_FPS}; a drag, a fling and the reorient at the full
 * display rate. The owner starts and stops the loop (`setRunning`): it runs only while the backdrop is on
 * a visible stage, and a hidden tab pauses it here. In **still mode** nothing loops and the planet
 * redraws only when dragged: under reduced motion, and on a renderer measured too slow to animate
 * (SwiftShader manages ~3fps here and drags the whole shell down with it).
 *
 * The planet's screen frame comes from `planetFrame` (`backdropLayout.ts`). The camera's view offset
 * puts its optical axis through the planet's centre wherever that lands, so the globe stays round
 * even when it sits far off-centre.
 *
 * `new PlanetStage` throws when the browser refuses a WebGL context; the owner catches it and shows
 * the backdrop's type alone.
 */
import * as THREE from "three";
import { planetFrame, type PlanetFrame } from "./backdropLayout";
import { mulberry32 } from "./mulberry32";
import type { PlanetProfile } from "./planetProfiles";
import { ATMOSPHERE_FRAGMENT, BAKE_VERTEX, POINTS_FRAGMENT, POINTS_VERTEX, SPHERE_VERTEX, TRAFFIC_VERTEX } from "./planetShaders";
import { atRest, drag, grab, needsFullRate, release, restingSpin, stepSpin, type Spin, type SpinMode, type SpinPhase } from "./spin";
import { WindLayer } from "./WindLayer";

/** What the e2e harness and the fps probe read (`window.__backdropDebug`). */
export type BackdropDebug = {
  running: boolean;
  frames: number;
  /** Animation-frame callbacks, drawn or skipped by the idle cap: zero growth means no rAF at all. */
  ticks: number;
  mode: SpinMode;
  phase: SpinPhase;
  timeScale: number;
  /** Frames rendered over the last full second. */
  fps: number;
  /** True once the loop measured too slow (a software renderer) and fell back to still mode. */
  degraded: boolean;
  yaw: number;
  pitch: number;
  frame: PlanetFrame;
};

declare global {
  // Module augmentation of the global `Window` requires `interface` (declaration merging), the same
  // sanctioned exception `DiceOverlay.tsx`'s `__diceStageDebug` takes.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    /** TEST/DIAGNOSTIC ONLY — the backdrop's loop state, read by the e2e probe and the fps check. */
    __backdropDebug?: BackdropDebug;
  }
}

/** The camera's distance from the planet, in planet radii. */
const CAMERA_DISTANCE = 6;
const STAR_COUNT = 1400;
/**
 * The drawing buffer's pixel-ratio cap. A soft-edged planet reads the same at 1.5 as at 2, and 1.5
 * costs about a third less GPU per frame (`backdrop-play-pause` 1.2).
 */
export const MAX_PIXEL_RATIO = 1.5;
/**
 * The draw rate for the slow idle spin and the glide to a stop. Frame cost is mostly fixed per-frame
 * overhead, so this halves the CPU of an idling planet; motion is `dt`-scaled, so it doesn't judder.
 */
export const IDLE_FPS = 30;

export class PlanetStage {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);
  /** Positioned at the planet's centre; scaled so one unit is one planet radius. */
  private readonly planet = new THREE.Group();
  /** Pitch from the spin, applied over the fixed axial tilt. */
  private readonly tilt = new THREE.Group();
  /** Yaw from the spin: the surface, its clouds and the traffic ring all turn with it. */
  private readonly turn = new THREE.Group();
  private readonly planetMaterial: THREE.ShaderMaterial;
  private readonly trafficMaterial: THREE.ShaderMaterial;
  private readonly profile: PlanetProfile;
  private readonly wind: WindLayer | undefined;
  /** Set by `stars()` during construction. */
  private starMaterial!: THREE.ShaderMaterial;
  private readonly disposables: Array<{ dispose(): void }> = [];
  private readonly reducedMotion: boolean;
  private readonly onDegrade: () => void;
  private mode: SpinMode;

  private spin: Spin = restingSpin();
  private frame: PlanetFrame = planetFrame({ width: 1, height: 1 });
  private raf = 0;
  private wanted = false;
  private last = 0;
  /** The shared clock's reading: seconds of `dt * timeScale`, which drives the clouds and traffic. */
  private time = 0;
  private frames = 0;
  private ticks = 0;
  private fpsWindowStart = 0;
  private fpsWindowFrames = 0;
  private fps = 0;
  private slowWindows = 0;
  /** Too slow to animate: the planet holds still and redraws only when dragged. */
  private degraded = false;
  private size = { width: 1, height: 1, pixelRatio: 1 };
  private pointer: { id: number; x: number; y: number; t: number } | null = null;

  constructor(canvas: HTMLCanvasElement, opts: { profile: PlanetProfile; reducedMotion: boolean; mode: SpinMode; onDegrade: () => void }) {
    const profile = opts.profile;
    this.profile = profile;
    this.reducedMotion = opts.reducedMotion;
    this.mode = opts.mode;
    this.onDegrade = opts.onDegrade;
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
    this.renderer.setClearColor(0x000000, 0);

    // The key light, in view space. Verdigris's sits upper left, slightly in front, so the terminator falls right.
    const light = new THREE.Vector3(...profile.light).normalize();
    const atmo = new THREE.Color(...profile.atmosphere);
    const surface = this.bake(profile.shaders.surface, { width: 2048, height: 1024, out: 0 });
    const clouds = this.bake(profile.shaders.clouds, { width: 1024, height: 512, out: 0 });
    const lights = profile.nightLights ? this.bake(profile.shaders.surface, { width: 2048, height: 1024, out: 1 }) : null;

    this.planetMaterial = new THREE.ShaderMaterial({
      vertexShader: SPHERE_VERTEX,
      fragmentShader: profile.shaders.planet,
      uniforms: {
        uSurface: { value: surface },
        uClouds: { value: clouds },
        uLights: { value: lights },
        uLight: { value: light },
        uCloudShift: { value: 0 },
        uTime: { value: 0 },
        uAtmo: { value: atmo },
      },
    });
    const sphere = this.own(new THREE.SphereGeometry(1, 160, 112));
    this.turn.add(new THREE.Mesh(sphere, this.own(this.planetMaterial)));

    if (profile.shell !== undefined) {
      const atmosphere = new THREE.Mesh(
        sphere,
        this.own(
          new THREE.ShaderMaterial({
            vertexShader: SPHERE_VERTEX,
            fragmentShader: ATMOSPHERE_FRAGMENT,
            uniforms: { uLight: { value: light }, uAtmo: { value: atmo } },
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false,
          }),
        ),
      );
      atmosphere.scale.setScalar(profile.shell);
      this.planet.add(atmosphere);
    }

    this.trafficMaterial = this.own(
      new THREE.ShaderMaterial({
        vertexShader: TRAFFIC_VERTEX,
        fragmentShader: POINTS_FRAGMENT,
        uniforms: { uTime: { value: 0 }, uPixelRatio: { value: 1 }, uColor: { value: new THREE.Color(...profile.traffic.color) } },
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      }),
    );
    const ring = new THREE.Group();
    ring.rotation.x = 0.2;
    ring.rotation.z = -0.06;
    ring.add(new THREE.Points(this.trafficGeometry(profile.traffic.count), this.trafficMaterial));
    ring.add(this.orbitLine(1.42, atmo));
    this.turn.add(ring);

    this.turn.rotation.order = "YXZ";
    this.tilt.rotation.z = profile.axialTilt;
    this.tilt.add(this.turn);
    this.planet.add(this.tilt);
    this.scene.add(this.planet);
    this.scene.add(this.stars());

    if (profile.wind !== undefined) {
      this.wind = this.own(new WindLayer(this.renderer, { wind: profile.wind, light }));
      this.scene.add(this.wind.streaks);
    }

    document.addEventListener("visibilitychange", this.onVisibility);
  }

  /** Size the drawing buffer to the canvas's CSS box (the viewport) and re-frame the planet. */
  setSize(width: number, height: number, pixelRatio: number): void {
    this.size = { width, height, pixelRatio };
    const dpr = this.degraded ? 1 : Math.min(pixelRatio, MAX_PIXEL_RATIO);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.frame = planetFrame({ width, height }, this.profile.placement);
    const { cx, cy, r } = this.frame;
    // Pixels per world unit at the planet's depth, then a virtual frame centred on the planet.
    const fullW = 2 * Math.max(cx, width - cx);
    const fullH = 2 * Math.max(cy, height - cy);
    const pxPerUnit = r * 1.0;
    this.camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(fullH / 2 / pxPerUnit / CAMERA_DISTANCE));
    this.camera.aspect = fullW / fullH;
    this.camera.position.set(0, 0, CAMERA_DISTANCE);
    this.camera.setViewOffset(fullW, fullH, fullW / 2 - cx, fullH / 2 - cy, width, height);
    this.camera.updateProjectionMatrix();
    this.trafficMaterial.uniforms.uPixelRatio!.value = dpr;
    this.starMaterial.uniforms.uPixelRatio!.value = dpr;
    this.wind?.setSize(width, height);
    if (this.raf === 0) this.render();
  }

  /**
   * Run or pause the render loop. A hidden tab pauses it regardless. In still mode (reduced motion,
   * or a renderer too slow to animate) nothing loops: one frame now, and one per drag move.
   */
  setRunning(on: boolean): void {
    this.wanted = on;
    this.sync();
  }

  /** Play keeps the idle spin going; pause lets it settle and stop. A resting planet wakes on play. */
  setMode(mode: SpinMode): void {
    this.mode = mode;
    this.sync();
  }

  pointerDown(e: PointerEvent): void {
    this.pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp };
    this.spin = grab(this.spin);
    // A drag revives a resting loop at once.
    this.sync();
  }

  pointerMove(e: PointerEvent): void {
    const p = this.pointer;
    if (p === null || p.id !== e.pointerId) return;
    this.spin = drag(this.spin, { dx: e.clientX - p.x, dy: e.clientY - p.y, dt: (e.timeStamp - p.t) / 1000, radius: this.frame.r });
    this.pointer = { id: p.id, x: e.clientX, y: e.clientY, t: e.timeStamp };
    if (!this.raf) this.render();
  }

  pointerUp(e: PointerEvent): void {
    if (this.pointer?.id !== e.pointerId) return;
    // A pause before letting go is a placement, not a fling; and nothing flings in still mode.
    if (e.timeStamp - this.pointer.t > 80 || this.still()) this.spin = { ...this.spin, vYaw: 0, vPitch: 0 };
    this.pointer = null;
    this.spin = release(this.spin);
  }

  dispose(): void {
    this.wanted = false;
    this.sync();
    document.removeEventListener("visibilitychange", this.onVisibility);
    for (const d of this.disposables) d.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  debug(): BackdropDebug {
    return {
      running: this.raf !== 0,
      frames: this.frames,
      ticks: this.ticks,
      mode: this.mode,
      phase: this.spin.phase,
      timeScale: this.spin.timeScale,
      fps: this.fps, degraded: this.degraded,
      yaw: this.spin.yaw,
      pitch: this.spin.pitch,
      frame: this.frame,
    };
  }

  // ── internals ────────────────────────────────────────────────────────────────

  private readonly onVisibility = () => this.sync();

  private still(): boolean {
    return this.reducedMotion || this.degraded;
  }

  private sync(): void {
    const visible = this.wanted && !document.hidden;
    // A resting planet in pause draws nothing; play or a drag takes it out of rest.
    const run = visible && !this.still() && !(atRest(this.spin) && this.mode === "pause");
    if (visible && !run) this.render();
    if (run && this.raf === 0) {
      this.last = performance.now();
      this.fpsWindowStart = this.last;
      this.fpsWindowFrames = 0;
      this.raf = requestAnimationFrame(this.tick);
    } else if (!run && this.raf !== 0) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  private readonly tick = (now: number) => {
    this.ticks++;
    // Slow motion draws at the idle cap; half a 120 Hz frame of slack keeps 60 Hz panels on every other frame.
    if (!needsFullRate(this.spin) && now - this.last < 1000 / IDLE_FPS - 4) {
      this.raf = requestAnimationFrame(this.tick);
      return;
    }
    // The first frame's timestamp can precede the `performance.now()` that seeded `last`.
    const dt = Math.max(0, Math.min((now - this.last) / 1000, 0.1));
    this.last = now;
    this.spin = stepSpin(this.spin, dt, this.mode);
    this.time += dt * this.spin.timeScale;
    this.render();
    this.frames++;
    this.fpsWindowFrames++;
    if (now - this.fpsWindowStart >= 1000) {
      this.fps = (this.fpsWindowFrames * 1000) / (now - this.fpsWindowStart);
      this.fpsWindowStart = now;
      this.fpsWindowFrames = 0;
      // Three slow seconds in a row: this renderer can't animate a full-viewport planet without
      // dragging the whole shell down with it, so hold still at 1x and redraw only on drags. Under
      // the idle cap the bar drops with the cap, so a capped second never reads as slow.
      const floor = needsFullRate(this.spin) ? 24 : IDLE_FPS * 0.6;
      this.slowWindows = this.fps < floor ? this.slowWindows + 1 : 0;
      if (this.slowWindows >= 3) {
        this.degraded = true;
        this.raf = 0;
        this.setSize(this.size.width, this.size.height, this.size.pixelRatio);
        this.onDegrade();
        return;
      }
    }
    // Settled in pause: the frame just drawn is the last until a drag or play.
    if (atRest(this.spin) && this.mode === "pause") {
      this.raf = 0;
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  private render(): void {
    this.turn.rotation.y = this.spin.yaw;
    this.tilt.rotation.x = this.spin.pitch;
    // Clouds drift a little faster than the ground under them; traffic keeps its own orbits; the wind
    // blows. All read the shared clock, which only advances in the loop, so in still mode and at
    // rest they hold.
    this.planetMaterial.uniforms.uCloudShift!.value = this.time * this.profile.cloudDrift;
    this.planetMaterial.uniforms.uTime!.value = this.time;
    this.trafficMaterial.uniforms.uTime!.value = this.time;
    this.wind?.setTime(this.time);
    this.renderer.render(this.scene, this.camera);
    this.wind?.composite(this.renderer, this.camera);
  }

  private own<T extends { dispose(): void }>(thing: T): T {
    this.disposables.push(thing);
    return thing;
  }

  /** Render one bake shader into an equirectangular map and keep the texture. `out` picks a bake's output, where it has two. */
  private bake(fragmentShader: string, { width, height, out }: { width: number; height: number; out: number }): THREE.Texture {
    const target = this.own(
      new THREE.WebGLRenderTarget(width, height, {
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.ClampToEdgeWrapping,
        depthBuffer: false,
      }),
    );
    target.texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    const material = new THREE.ShaderMaterial({
      vertexShader: BAKE_VERTEX,
      fragmentShader,
      uniforms: {
        uSeed: { value: new THREE.Vector3(...this.profile.seed) },
        uOut: { value: out },
        uBleach: { value: this.profile.bleach },
      },
      depthTest: false,
    });
    const scene = new THREE.Scene();
    const quad = new THREE.Mesh(geometry, material);
    quad.frustumCulled = false;
    scene.add(quad);
    this.renderer.setRenderTarget(target);
    this.renderer.render(scene, new THREE.Camera());
    this.renderer.setRenderTarget(null);
    geometry.dispose();
    material.dispose();
    return target.texture;
  }

  /** Orbital traffic: convoys strung along a few lanes, each dot on its own orbit. */
  private trafficGeometry(count: number): THREE.BufferGeometry {
    const angle = new Float32Array(count);
    const radius = new Float32Array(count);
    const lift = new Float32Array(count);
    const size = new Float32Array(count);
    const glow = new Float32Array(count);
    const rand = mulberry32(621);
    const lanes = [1.3, 1.36, 1.42, 1.5];
    for (let i = 0; i < count; i++) {
      // A third ride in a few tight convoys; the rest are scattered.
      const convoy = i % 3 === 0;
      const lane = lanes[Math.floor(rand() * lanes.length)]!;
      angle[i] = convoy ? Math.floor(rand() * 5) * 1.26 + rand() * 0.12 : rand() * Math.PI * 2;
      radius[i] = lane + (rand() - 0.5) * 0.015;
      lift[i] = (rand() - 0.5) * 0.02;
      size[i] = 2 + rand() * 2 + (rand() < 0.08 ? 3 : 0);
      glow[i] = 0.6 + rand() * 0.6;
    }
    const g = this.own(new THREE.BufferGeometry());
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    g.setAttribute("aAngle", new THREE.BufferAttribute(angle, 1));
    g.setAttribute("aRadius", new THREE.BufferAttribute(radius, 1));
    g.setAttribute("aLift", new THREE.BufferAttribute(lift, 1));
    g.setAttribute("aSize", new THREE.BufferAttribute(size, 1));
    g.setAttribute("aGlow", new THREE.BufferAttribute(glow, 1));
    // Positions are computed in the shader; give the bounds by hand so the points never cull.
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1.6);
    return g;
  }

  private orbitLine(radius: number, color: THREE.Color): THREE.LineLoop {
    const pts: number[] = [];
    for (let i = 0; i < 256; i++) {
      const a = (i / 256) * Math.PI * 2;
      pts.push(Math.cos(a) * radius, 0, Math.sin(a) * radius);
    }
    const g = this.own(new THREE.BufferGeometry());
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    const m = this.own(
      new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.16, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    return new THREE.LineLoop(g, m);
  }

  private stars(): THREE.Points {
    const rand = mulberry32(42);
    const pos = new Float32Array(STAR_COUNT * 3);
    const size = new Float32Array(STAR_COUNT);
    const glow = new Float32Array(STAR_COUNT);
    for (let i = 0; i < STAR_COUNT; i++) {
      const z = rand() * 2 - 1;
      const a = rand() * Math.PI * 2;
      const s = Math.sqrt(1 - z * z);
      pos.set([Math.cos(a) * s * 80, Math.sin(a) * s * 80, z * 80 - 20], i * 3);
      size[i] = 1 + rand() * rand() * 2.2;
      glow[i] = 0.12 + rand() * rand() * 0.75;
    }
    const g = this.own(new THREE.BufferGeometry());
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("aSize", new THREE.BufferAttribute(size, 1));
    g.setAttribute("aGlow", new THREE.BufferAttribute(glow, 1));
    this.starMaterial = this.own(
      new THREE.ShaderMaterial({
        vertexShader: POINTS_VERTEX,
        fragmentShader: POINTS_FRAGMENT,
        uniforms: { uPixelRatio: { value: 1 }, uColor: { value: new THREE.Color(0.86, 0.92, 0.95) } },
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      }),
    );
    return new THREE.Points(g, this.starMaterial);
  }
}
